import { useState, useEffect } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Input, Select, Tabs, Divider } from 'antd';
import { ImCancelCircle } from 'react-icons/im';
import { toast } from 'react-toastify';
import { BeatLoader } from "react-spinners";
import { createMedication, updateMedication } from '../../../services/medications';
import './AddMedicationModal.css';

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  presentation: Yup.string().required('Presentation is required'),
  price: Yup.string(),
  activeMoleculesAndDosage: Yup.string().required('Active molecules and dosage are required'),
  therapeuticClass: Yup.string().required('Therapeutic class is required'),
  subClass: Yup.string(),
  otherInfo: Yup.string()
});

const AddMedicationModal = ({ onClose, onContentAdded, editData = null }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [therapeuticClasses, setTherapeuticClasses] = useState([
    'Analgesic', 'Antibiotic', 'Antiviral', 'Antifungal', 'Antiparasitic',
    'Cardiovascular', 'Respiratory', 'Gastrointestinal', 'Neurological',
    'Endocrine', 'Immunosuppressant', 'Vaccine', 'Other'
  ]);
  
  const isEditMode = !!editData;

  const initialValues = isEditMode ? {
    ...editData,
    // Ensure all required fields exist even if not in editData
    nameFr: editData.nameFr || '',
    presentationFr: editData.presentationFr || '',
    activeMoleculesAndDosageFr: editData.activeMoleculesAndDosageFr || '',
    therapeuticClassFr: editData.therapeuticClassFr || '',
    subClassFr: editData.subClassFr || '',
    otherInfoFr: editData.otherInfoFr || ''
  } : {
    name: '',
    presentation: '',
    price: '',
    activeMoleculesAndDosage: '',
    therapeuticClass: '',
    subClass: '',
    otherInfo: '',
    nameFr: '',
    presentationFr: '',
    activeMoleculesAndDosageFr: '',
    therapeuticClassFr: '',
    subClassFr: '',
    otherInfoFr: ''
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      
      // Prepare data for submission
      const medicationData = {
        ...values,
        // Format the price if needed
        price: values.price ? values.price.toString() : ''
      };

      if (isEditMode) {
        await updateMedication(editData.id, medicationData);
        toast.success('Medication updated successfully');
      } else {
        await createMedication(medicationData);
        toast.success('Medication created successfully');
      }

      onContentAdded();
    } catch (error) {
      toast.error(isEditMode ? 'Error updating medication' : 'Error creating medication');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  return (
    <div className="modal-wrapper">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{isEditMode ? 'Edit Medication' : 'Add New Medication'}</h2>
          <ImCancelCircle 
            className="close-icon"
            onClick={onClose} 
            size={22} 
          />
        </div>

        <div className="modal-content">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, setFieldValue }) => (
              <Form className="form-container">
                <Tabs 
                  activeKey={activeTab} 
                  onChange={handleTabChange}
                  className="language-tabs"
                >
                  <TabPane tab="English" key="1">
                    <div className="english-section">
                      <div className="form-group">
                        <label>Name *</label>
                        <Input
                          name="name"
                          onChange={handleChange}
                          value={values.name}
                          className="custom-input"
                          placeholder="Medication name"
                        />
                        {errors.name && touched.name && (
                          <div className="error-message">{errors.name}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Presentation *</label>
                        <Input
                          name="presentation"
                          onChange={handleChange}
                          value={values.presentation}
                          className="custom-input"
                          placeholder="e.g., Tablet 10mg, Syrup 5mg/ml"
                        />
                        {errors.presentation && touched.presentation && (
                          <div className="error-message">{errors.presentation}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Price</label>
                        <Input
                          name="price"
                          onChange={handleChange}
                          value={values.price}
                          className="custom-input"
                          placeholder="e.g., 1000 FCFA"
                        />
                      </div>

                      <div className="form-group">
                        <label>Active Molecules and Dosage *</label>
                        <TextArea
                          name="activeMoleculesAndDosage"
                          onChange={handleChange}
                          value={values.activeMoleculesAndDosage}
                          className="custom-textarea"
                          rows={3}
                          placeholder="e.g., Paracetamol 500mg, Caffeine 65mg"
                        />
                        {errors.activeMoleculesAndDosage && touched.activeMoleculesAndDosage && (
                          <div className="error-message">{errors.activeMoleculesAndDosage}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Therapeutic Class *</label>
                        <Select
                          className="custom-select"
                          onChange={(value) => setFieldValue('therapeuticClass', value)}
                          value={values.therapeuticClass}
                          placeholder="Select therapeutic class"
                          showSearch
                          filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                        >
                          {therapeuticClasses.map((className) => (
                            <Option key={className} value={className}>
                              {className}
                            </Option>
                          ))}
                        </Select>
                        {errors.therapeuticClass && touched.therapeuticClass && (
                          <div className="error-message">{errors.therapeuticClass}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Sub-Class (Optional)</label>
                        <Input
                          name="subClass"
                          onChange={handleChange}
                          value={values.subClass}
                          className="custom-input"
                          placeholder="e.g., Broad-spectrum, Beta-lactam"
                        />
                      </div>

                      <div className="form-group">
                        <label>Additional Information (Optional)</label>
                        <TextArea
                          name="otherInfo"
                          onChange={handleChange}
                          value={values.otherInfo}
                          className="custom-textarea"
                          rows={4}
                          placeholder="Additional information about the medication"
                        />
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tab="French (Optional)" key="2">
                    <div className="french-section">
                      <div className="form-group">
                        <label>Name (French)</label>
                        <Input
                          name="nameFr"
                          onChange={handleChange}
                          value={values.nameFr}
                          className="custom-input"
                          placeholder="French medication name"
                        />
                      </div>

                      <div className="form-group">
                        <label>Presentation (French)</label>
                        <Input
                          name="presentationFr"
                          onChange={handleChange}
                          value={values.presentationFr}
                          className="custom-input"
                          placeholder="e.g., Comprimé 10mg, Sirop 5mg/ml"
                        />
                      </div>

                      <div className="form-group">
                        <label>Active Molecules and Dosage (French)</label>
                        <TextArea
                          name="activeMoleculesAndDosageFr"
                          onChange={handleChange}
                          value={values.activeMoleculesAndDosageFr}
                          className="custom-textarea"
                          rows={3}
                          placeholder="French active molecules and dosage"
                        />
                      </div>

                      <div className="form-group">
                        <label>Therapeutic Class (French)</label>
                        <Input
                          name="therapeuticClassFr"
                          onChange={handleChange}
                          value={values.therapeuticClassFr}
                          className="custom-input"
                          placeholder="French therapeutic class"
                        />
                      </div>

                      <div className="form-group">
                        <label>Sub-Class (French)</label>
                        <Input
                          name="subClassFr"
                          onChange={handleChange}
                          value={values.subClassFr}
                          className="custom-input"
                          placeholder="French sub-class"
                        />
                      </div>

                      <div className="form-group">
                        <label>Additional Information (French)</label>
                        <TextArea
                          name="otherInfoFr"
                          onChange={handleChange}
                          value={values.otherInfoFr}
                          className="custom-textarea"
                          rows={4}
                          placeholder="Additional information in French"
                        />
                      </div>
                    </div>
                  </TabPane>
                </Tabs>

                <div className="button-container">
                  {loading ? (
                    <BeatLoader color="#3080ED" />
                  ) : (
                    <button type="submit" className="submit-button">
                      {isEditMode ? 'Update Medication' : 'Add Medication'}
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="modal-overlay" onClick={onClose} />
    </div>
  );
};

export default AddMedicationModal;